import React, { useRef, useEffect } from "react";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { ACCENT } from "../constants/quotationConstants";
import { computeDragSnap, computeResizeSnap } from "../utils/snapUtils";
import ElementContent from "./ElementContent";

export default function CanvasElement({
  el,
  selected,
  scale,
  onSelect,
  onChange,
  onDelete,
  editing,
  onStartEdit,
  onStopEdit,
  elements = [],
  pageW,
  pageH,
  margin,
  onDragGuides,
}) {
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const editableRef = useRef(null);
  const tdRefs = useRef({});

  useEffect(() => {
    if (editing && editableRef.current) {
      // Set the starting text only once, imperatively — do NOT rely on React
      // children for this while contentEditable is on. If children stayed
      // bound to el.content, any unrelated re-render during typing (e.g. the
      // products list finishing its load) would make React reset the DOM
      // back to the old el.content, wiping out what the user just typed.
      editableRef.current.innerText = el.content || "";
      editableRef.current.focus();
      const range = document.createRange();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [editing]);

  // Same imperative-init treatment for table cells: populate each cell's
  // current text once when edit mode turns on, instead of letting React's
  // children prop drive it while the cells are contentEditable.
  useEffect(() => {
    if (editing && el.type === "table") {
      (el.rows || []).forEach((row, ri) => {
        row.forEach((cell, ci) => {
          const node = tdRefs.current[`${ri}-${ci}`];
          if (node) node.innerText = cell ?? "";
        });
      });
    }
  }, [editing]);

  const onPointerDownMove = (e) => {
    if (e.target.dataset.noDrag) return;
    if (editing) return;
    // Selecting/dragging a different element while another one is still
    // mid-edit: blur the other one first so its typed text is committed
    // before its editing state gets cleared.
    if (document.activeElement && document.activeElement !== document.body && document.activeElement !== e.currentTarget) {
      document.activeElement.blur();
    }
    e.stopPropagation();
    onSelect(el.id);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y };
    const siblings = elements.filter((s) => s.id !== el.id);
    const move = (ev) => {
      if (!dragRef.current) return;
      const dx = (ev.clientX - dragRef.current.startX) / scale;
      const dy = (ev.clientY - dragRef.current.startY) / scale;
      const proposed = { x: dragRef.current.origX + dx, y: dragRef.current.origY + dy, w: el.w, h: el.h };
      const snap = computeDragSnap(proposed, siblings, pageW, pageH, margin);
      onChange(el.id, { x: snap.x, y: snap.y });
      onDragGuides && onDragGuides(snap.guides);
    };
    const up = () => {
      dragRef.current = null;
      onDragGuides && onDragGuides([]);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // Alt held while resizing = grow/shrink symmetrically from the center
  // (both edges move together) instead of only from the bottom-right.
  const onResizeDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, w: el.w, h: el.h, x: el.x, y: el.y };
    const siblings = elements.filter((s) => s.id !== el.id);
    const minW = 24;
    const minH = el.type === "line" ? 2 : 20;
    const move = (ev) => {
      if (!resizeRef.current) return;
      const { startX, startY, w, h, x, y } = resizeRef.current;
      const dw = (ev.clientX - startX) / scale;
      const dh = (ev.clientY - startY) / scale;

      if (ev.altKey) {
        const newW = Math.max(minW, Math.round(w + dw * 2));
        const newH = Math.max(minH, Math.round(h + dh * 2));
        const newX = Math.round(x - (newW - w) / 2);
        const newY = Math.round(y - (newH - h) / 2);
        onChange(el.id, { x: newX, y: newY, w: newW, h: newH });
        onDragGuides && onDragGuides([]);
      } else {
        const newW = Math.max(minW, Math.round(w + dw));
        const newH = Math.max(minH, Math.round(h + dh));
        const snap = computeResizeSnap({ x, y, w: newW, h: newH }, siblings, pageW, pageH, margin);
        onChange(el.id, { w: snap.w, h: snap.h });
        onDragGuides && onDragGuides(snap.guides);
      }
    };
    const up = () => {
      resizeRef.current = null;
      onDragGuides && onDragGuides([]);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const content = (() => {
    if (el.type === "text" || el.type === "heading") {
      return (
        <div
          ref={editableRef}
          data-no-drag={editing ? true : undefined}
          contentEditable={editing}
          suppressContentEditableWarning
          onPointerDown={(e) => {
            if (editing) e.stopPropagation();
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            onStartEdit(el.id);
          }}
          onBlur={(e) => {
            onChange(el.id, { content: e.currentTarget.innerText });
            onStopEdit();
          }}
          style={{
            width: "100%",
            height: "100%",
            outline: "none",
            cursor: editing ? "text" : "inherit",
            fontSize: el.fontSize,
            fontWeight: el.fontWeight,
            fontStyle: el.fontStyle || "normal",
            textDecoration: el.textDecoration || "none",
            color: el.color,
            textAlign: el.align,
            whiteSpace: "pre-wrap",
            lineHeight: 1.4,
            fontFamily: "Arial, sans-serif",
            overflow: "hidden",
          }}
        >
          {editing ? undefined : el.content}
        </div>
      );
    }
    if (el.type === "table") {
      const rowPct = 100 / el.rows.length;
      return (
        <table
          data-no-drag={editing ? true : undefined}
          onPointerDown={(e) => {
            if (editing) e.stopPropagation();
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            onStartEdit(el.id);
          }}
          style={{ width: "100%", height: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: el.fontSize, fontFamily: "Arial, sans-serif" }}
        >
          <tbody>
            {el.rows.map((row, ri) => (
              <tr key={ri} style={{ height: `${rowPct}%` }}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    ref={(node) => {
                      tdRefs.current[`${ri}-${ci}`] = node;
                    }}
                    contentEditable={editing}
                    suppressContentEditableWarning
                    onBlur={(e) => {
                      const rows = el.rows.map((r) => [...r]);
                      rows[ri][ci] = e.currentTarget.innerText;
                      onChange(el.id, { rows });
                      onStopEdit();
                    }}
                    style={{
                      border: "1px solid #D6DCE5",
                      padding: "5px 8px",
                      outline: "none",
                      background: ri === 0 ? ACCENT : "#fff",
                      color: ri === 0 ? "#fff" : "#222",
                      fontWeight: ri === 0 ? 600 : 400,
                      cursor: editing ? "text" : "inherit",
                      overflow: "hidden",
                    }}
                  >
                    {editing ? undefined : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return <ElementContent el={el} />;
  })();

  return (
    <div
      onPointerDown={onPointerDownMove}
      style={{
        position: "absolute",
        left: el.x,
        top: el.y,
        width: el.w,
        height: el.h,
        outline: selected ? `1.5px solid ${ACCENT}` : "1.5px solid transparent",
        cursor: editing ? "text" : "grab",
        boxSizing: "border-box",
      }}
    >
      {content}
      {selected && !editing && (
        <>
          <div
            data-no-drag
            title="Drag to resize (hold Alt to expand symmetrically)"
            onPointerDown={onResizeDown}
            style={{
              position: "absolute",
              right: -6,
              bottom: -6,
              width: 12,
              height: 12,
              background: ACCENT,
              borderRadius: "50%",
              border: "2px solid #fff",
              cursor: "nwse-resize",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
            }}
          />
          <Button
            data-no-drag
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onDelete(el.id)}
            title="Delete"
            danger
            type="primary"
            shape="circle"
            size="small"
            icon={<CloseOutlined style={{ fontSize: 10 }} />}
            style={{ position: "absolute", top: -12, right: -12, width: 22, height: 22, minWidth: 22, padding: 0, border: "2px solid #fff" }}
          />
        </>
      )}
    </div>
  );
}